import "leaflet/dist/leaflet.css";
import "./style.css";

import { ApiClient, isAbortError } from "./api";
import {
  defaultConfig,
  isLocalDevelopment,
  isValidBaseUrl,
  loadConfig,
  normalizeBaseUrl,
  saveConfig,
  type AppConfig,
} from "./config";
import { byId, clear, el, icon, setBusy, setHidden, setText } from "./dom";
import { GEOLOCATION_MESSAGES, humanizeError } from "./errors";
import { downloadCsv, parseHouseholdsCsv, toCsv } from "./csv";
import {
  formatCountdown,
  formatRelative,
} from "./format";
import { LiveMap } from "./map";
import { Poller } from "./poller";
import { confirmAction } from "./confirm";
import { ToastCenter } from "./toast";
import type {
  CreateHouseholdRequest,
  Device,
  GeneratedCode,
  Household,
  IssuedDevice,
  LiveResponse,
  NotificationEvent,
  OperatorStats,
  PQRSStatus,
} from "./types";
import { initAlerts } from "./views/alerts";
import { initDevices, type DeviceForm } from "./views/devices";
import { initFleet } from "./views/fleet";
import { initHouseholds } from "./views/households";
import { initOperation, type LoadStatus } from "./views/operation";
import { initPqrs } from "./views/pqrs";
import { initStats } from "./views/stats";

const LIVE_INTERVAL_MS = 3000;
const ALERTS_INTERVAL_MS = 3000;
const STATS_INTERVAL_MS = 6000;
const READY_INTERVAL_MS = 10000;
const CODE_TICK_MS = 30000;
const CODE_TTL_MINUTES = 480;

const HOUSEHOLDS_PAGE_SIZE = 8;
const HOUSEHOLDS_MAP_LIMIT = 200;
const ALERTS_PAGE_SIZE = 25;

type ConnectionState = "unknown" | "ok" | "degraded" | "error";

const CONNECTION_LABEL: Record<ConnectionState, string> = {
  unknown: "Conectando…",
  ok: "Conectado",
  degraded: "Base de datos no disponible",
  error: "Sin conexión",
};

const CONNECTION_CLASS: Record<ConnectionState, string> = {
  unknown: "chip chip--unknown",
  ok: "chip chip--ok",
  degraded: "chip chip--warn",
  error: "chip chip--error",
};

export class Dashboard {
  private readonly toasts = new ToastCenter(byId<HTMLDivElement>("toasts"));
  private readonly map = new LiveMap();

  private readonly operation = initOperation({
    onReloadFleet: () => void this.loadFleet(),
    onGenerate: () => void this.generateCode(),
    onCopyCode: () => void this.copyCode(),
  });

  private readonly stats = initStats({
    onRetry: () => this.statsPoller.runNow(),
  });

  private readonly alerts = initAlerts({
    onRetry: () => this.alertsPoller.runNow(),
    onLoadMore: () => void this.loadMoreAlerts(),
    onClearFilter: () => this.clearAlertsFilter(),
    onExport: () => void this.exportAlertsCsv(),
  });

  private readonly households = initHouseholds({
    onReload: () => void this.loadHouseholds(),
    onSearch: (query) => this.searchHouseholds(query),
    onPage: (offset) => this.goToHouseholdsPage(offset),
    onEta: (household) => void this.requestEta(household),
    onEdit: (household) => this.startEditHousehold(household),
    onCancelEdit: () => this.cancelEditHousehold(),
    onDelete: (household) => void this.deleteHousehold(household),
    onViewAlerts: (household) => this.viewHouseholdAlerts(household),
    onPickOnMap: () => this.togglePickMode(),
    onGeolocate: () => this.useMyLocation(),
    onClearLocation: () => this.clearLocation(),
    onImport: (text) => void this.importHouseholdsCsv(text),
    onExport: () => void this.exportHouseholdsCsv(),
    onSubmit: (payload) => void this.saveHousehold(payload),
  });

  private readonly pqrs = initPqrs({
    onReload: (status) => void this.loadPqrs(status),
    onStatus: (id, status) => void this.updatePqrs(id, status),
  });

  private readonly fleet = initFleet({
    onReload: () => void this.loadFleet(),
    onCreateVehicle: (plate, label) => void this.saveVehicle(null, plate, label),
    onSaveVehicle: (id, plate, label) => void this.saveVehicle(id, plate, label),
    onCreateRoute: (name) => void this.saveRoute(null, name),
    onSaveRoute: (id, name) => void this.saveRoute(id, name),
  });

  private readonly devices = initDevices({
    onReload: () => void this.loadDevices(),
    onCreate: (form) => void this.createDevice(form),
    onRevoke: (device) => void this.revokeDevice(device),
    onCopyKey: () => void this.copyDeviceKey(),
  });

  private readonly connChip = byId<HTMLSpanElement>("conn-status");
  private readonly connText = byId<HTMLSpanElement>("conn-text");
  private readonly liveStatus = byId<HTMLParagraphElement>("live-status");
  private readonly mapContainer = byId<HTMLDivElement>("map");
  private readonly mapError = byId<HTMLDivElement>("map-error");
  private readonly mapPick = byId<HTMLDivElement>("map-pick");
  private readonly mapPickCancel = byId<HTMLButtonElement>("map-pick-cancel");
  private readonly recenterButton = byId<HTMLButtonElement>("recenter-map");
  private readonly settings = byId<HTMLDetailsElement>("settings");
  private readonly settingsMode = byId<HTMLParagraphElement>("settings-mode");
  private readonly readyDetail = byId<HTMLParagraphElement>("ready-detail");
  private readonly demoClearButton = byId<HTMLButtonElement>("demo-clear");
  private readonly configForm = byId<HTMLFormElement>("config-form");
  private readonly baseUrlField = byId<HTMLLabelElement>("base-url-field");
  private readonly baseUrlInput = byId<HTMLInputElement>("base-url");
  private readonly baseUrlNote = byId<HTMLParagraphElement>("base-url-note");
  private readonly apiKeyInput = byId<HTMLInputElement>("api-key");
  private readonly configResetButton = byId<HTMLButtonElement>("config-reset");

  private readonly readyPoller = new Poller(READY_INTERVAL_MS, () => this.runReadyCheck());
  private readonly livePoller = new Poller(LIVE_INTERVAL_MS, (signal) => this.pollLive(signal));
  private readonly alertsPoller = new Poller(ALERTS_INTERVAL_MS, (signal) => this.pollNotifications(signal));
  private readonly statsPoller = new Poller(STATS_INTERVAL_MS, (signal) => this.pollStats(signal));

  private client = new ApiClient(defaultConfig());
  private config: AppConfig = defaultConfig();
  private connection: ConnectionState = "unknown";

  private householdsList: Household[] = [];
  private householdsStatus: LoadStatus = "idle";
  private householdsQuery = "";
  private householdsOffset = 0;
  private householdsTotal = 0;
  private householdsRequest = 0;
  private editingId: string | null = null;
  private alertsFilter: Household | null = null;
  private alertsOlder: NotificationEvent[] = [];
  private alertsVisible: NotificationEvent[] = [];
  private alertsTotal = 0;
  private statsData: OperatorStats | null = null;
  private live: LiveResponse | null = null;
  private code: GeneratedCode | null = null;
  private pqrsStatus: LoadStatus = "idle";
  private devicesList: Device[] = [];
  private devicesStatus: LoadStatus = "idle";
  private issuedDevice: IssuedDevice | null = null;
  private hasFitted = false;

  start(): void {
    this.initSettings();
    this.recenterButton.addEventListener("click", () => {
      this.map.centerOnData(this.live?.sessions ?? [], this.householdsList);
    });
    this.mapPickCancel.addEventListener("click", () => this.stopPickMode());

    const config = loadConfig();
    this.baseUrlInput.value = config.baseUrl;
    this.apiKeyInput.value = config.apiKey;

    try {
      this.map.mount(this.mapContainer);
    } catch (error) {
      console.error("No se pudo inicializar el mapa", error);
      this.toasts.show({
        kind: "error",
        title: "El mapa no está disponible",
        message: "El resto del panel sigue funcionando; recarga la página para intentarlo de nuevo.",
      });
    }

    window.setInterval(() => this.operation.refreshClock(), CODE_TICK_MS);
    window.addEventListener("beforeunload", () => {
      this.readyPoller.stop();
      this.livePoller.stop();
      this.alertsPoller.stop();
      this.statsPoller.stop();
      this.map.destroy();
    });

    this.applyConfig(config, false);
  }

  private startPollers(): void {
    this.readyPoller.start();
    this.livePoller.start();
    this.alertsPoller.start();
    this.statsPoller.start();
  }

  private applyConfig(config: AppConfig, persist: boolean): void {
    if (persist) saveConfig(config);
    this.config = config;
    this.client = new ApiClient({ baseUrl: config.baseUrl, apiKey: config.apiKey });

    this.householdsList = [];
    this.householdsStatus = "idle";
    this.householdsQuery = "";
    this.householdsOffset = 0;
    this.householdsTotal = 0;
    this.householdsRequest += 1;
    this.alertsFilter = null;
    this.resetAlertsPages();
    this.statsData = null;
    this.live = null;
    this.code = null;
    this.devicesList = [];
    this.devicesStatus = "idle";
    this.pqrsStatus = "idle";
    this.issuedDevice = null;
    this.hasFitted = false;

    if (this.editingId !== null) {
      this.editingId = null;
      this.households.stopEdit();
    }
    this.operation.setCode(null);
    this.operation.setFleetStatus("loading", null, null);
    this.devices.setIssued(null);
    this.devices.setFleet(null);
    this.devices.setEndpoint(config.baseUrl);
    this.devices.setState({ status: "loading", devices: [], error: null });
    this.pqrs.setState({ status: "loading", items: [], total: 0, error: null });
    this.households.setSearchQuery("");
    this.households.setList({
      status: "loading",
      households: [],
      total: 0,
      offset: 0,
      limit: HOUSEHOLDS_PAGE_SIZE,
      query: "",
      error: null,
    });
    this.alerts.setFilter(null);
    this.alerts.reset();
    this.alerts.setToday(null);
    this.stats.setState({ status: "loading", stats: null, error: null });
    this.map.clearOverlays();
    this.map.setPickedLocation(null, null);
    this.stopPickMode();
    this.households.setLocation(null, null);
    this.liveStatus.textContent = "Sin datos todavía";
    this.hideMapError();
    this.setConnection("unknown");

    this.readyPoller.stop();
    this.livePoller.stop();
    this.alertsPoller.stop();
    this.statsPoller.stop();
    this.startPollers();
    void this.loadFleet();
    void this.loadHouseholds();
    void this.loadDevices();
    void this.loadPqrs("");
  }

  private setConnection(state: ConnectionState, detail?: string): void {
    const previous = this.connection;
    this.connection = state;
    this.connChip.className = CONNECTION_CLASS[state];
    setText(this.connText, CONNECTION_LABEL[state]);
    this.connChip.title = detail ?? "";
    setText(
      this.readyDetail,
      state === "unknown" ? "Comprobando el estado…" : (detail ?? ""),
    );
    if (state === "error" && previous === "ok") {
      this.toasts.show({
        kind: "error",
        title: "Se perdió la conexión",
        message: "Puedes seguir trabajando; el panel reintenta solo.",
      });
    }
  }

  private async runReadyCheck(): Promise<void> {
    try {
      const ready = await this.client.ready();
      if (ready.state === "ready") {
        const recovered = this.connection !== "ok";
        this.setConnection("ok", "Todo funcionando.");
        if (recovered) {
          if (this.householdsStatus !== "ready") void this.loadHouseholds();
          if (this.devicesStatus !== "ready") void this.loadDevices();
          if (this.pqrsStatus !== "ready") void this.loadPqrs(this.pqrs.filter());
          this.alertsPoller.runNow();
          this.statsPoller.runNow();
        }
      } else {
        this.setConnection(
          "degraded",
          "No podemos completar algunas acciones en este momento. Se reintenta solo.",
        );
      }
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("readyz", error);
      this.setConnection("error", humanizeError(error));
    }
  }

  private async pollLive(signal: AbortSignal): Promise<void> {
    try {
      const live = await this.client.live({ signal });
      this.live = live;
      this.map.updateTrucks(live.sessions);
      this.hideMapError();

      const count = live.sessions.length;
      if (count === 0) {
        setText(this.liveStatus, "Sin camiones en ruta");
      } else {
        const newest = live.sessions.reduce(
          (latest, session) => (Date.parse(session.last_seen_at) > Date.parse(latest) ? session.last_seen_at : latest),
          live.sessions[0]?.last_seen_at ?? live.now,
        );
        const plural = count === 1 ? "camión" : "camiones";
        setText(this.liveStatus, `${count} ${plural} en ruta · última señal ${formatRelative(newest)}`);
      }

      if (!this.hasFitted && (count > 0 || this.householdsList.length > 0)) {
        this.map.fitAll(live.sessions, this.householdsList);
        this.hasFitted = true;
      }
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("live", error);
      setText(this.liveStatus, "Sin datos en vivo");
      this.showMapError(humanizeError(error));
    }
  }

  private async pollStats(signal: AbortSignal): Promise<void> {
    if (this.statsData === null) this.stats.setState({ status: "loading", stats: null, error: null });
    try {
      const stats = await this.client.stats({ signal });
      this.statsData = stats;
      this.stats.setState({ status: "ready", stats, error: null });
      this.alerts.setToday(stats.alerts_today);
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("stats", error);
      this.stats.setState({
        status: "error",
        stats: this.statsData,
        error: humanizeError(error),
      });
    }
  }

  private resetAlertsPages(): void {
    this.alertsOlder = [];
    this.alertsVisible = [];
    this.alertsTotal = 0;
  }

  /** Une la ventana más reciente con las páginas antiguas ya cargadas, sin duplicados. */
  private mergeAlerts(...lists: readonly NotificationEvent[][]): NotificationEvent[] {
    const byId = new Map<string, NotificationEvent>();
    for (const list of lists) {
      for (const event of list) byId.set(event.id, event);
    }
    return [...byId.values()].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
  }

  private async pollNotifications(signal?: AbortSignal): Promise<void> {
    try {
      const data = await this.client.notifications(
        { limit: ALERTS_PAGE_SIZE, householdId: this.alertsFilter?.id },
        { signal },
      );
      if (data.total <= data.events.length) this.alertsOlder = [];
      this.alertsTotal = data.total;
      this.alertsVisible = this.mergeAlerts(data.events, this.alertsOlder);
      this.alerts.setState({
        status: "ready",
        events: this.alertsVisible,
        total: data.total,
        error: null,
      });
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("notifications", error);
      this.alerts.setState({ status: "error", events: [], total: 0, error: humanizeError(error) });
    }
  }

  private async loadMoreAlerts(): Promise<void> {
    if (this.alertsVisible.length >= this.alertsTotal) return;
    this.alerts.setLoadingMore(true);
    try {
      const data = await this.client.notifications({
        limit: ALERTS_PAGE_SIZE,
        offset: this.alertsVisible.length,
        householdId: this.alertsFilter?.id,
      });
      this.alertsTotal = data.total;
      this.alertsOlder = this.mergeAlerts(this.alertsOlder, data.events);
      this.alertsVisible = this.mergeAlerts(this.alertsVisible, this.alertsOlder);
      this.alerts.setState({
        status: "ready",
        events: this.alertsVisible,
        total: data.total,
        error: null,
      });
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("notifications:more", error);
      this.toasts.show({
        kind: "error",
        title: "No pudimos cargar más alertas",
        message: humanizeError(error),
      });
    } finally {
      this.alerts.setLoadingMore(false);
    }
  }

  private viewHouseholdAlerts(household: Household): void {
    this.alertsFilter = household;
    this.resetAlertsPages();
    this.alerts.setFilter(household);
    this.alertsPoller.runNow();
    document.getElementById("alerts-title")?.closest(".card")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  private clearAlertsFilter(): void {
    this.alertsFilter = null;
    this.resetAlertsPages();
    this.alerts.setFilter(null);
    this.alertsPoller.runNow();
  }

  private async loadFleet(): Promise<void> {
    this.operation.setFleetStatus("loading", null, null);
    this.devices.setFleet(null);
    this.fleet.setFleet(null);
    try {
      const fleet = await this.client.fleet();
      this.operation.setFleetStatus("ready", fleet, null);
      this.devices.setFleet(fleet);
      this.fleet.setFleet(fleet);
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("fleet", error);
      this.operation.setFleetStatus("error", null, humanizeError(error));
    }
  }

  private async saveVehicle(id: string | null, plate: string, label: string): Promise<void> {
    if (plate.trim() === "" || label.trim() === "") {
      this.toasts.show({ kind: "error", title: "Placa y etiqueta son obligatorias" });
      return;
    }
    this.fleet.setBusy(true);
    try {
      if (id === null) {
        await this.client.createVehicle({ plate: plate.trim(), label: label.trim() });
        this.toasts.show({ kind: "success", title: "Vehículo agregado", message: "Ya puedes generar códigos para él." });
      } else {
        await this.client.updateVehicle(id, { plate: plate.trim(), label: label.trim() });
        this.toasts.show({ kind: "success", title: "Vehículo actualizado" });
      }
      await this.loadFleet();
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("vehicle", error);
      this.toasts.show({ kind: "error", title: "No pudimos guardar el vehículo", message: humanizeError(error) });
    } finally {
      this.fleet.setBusy(false);
    }
  }

  private async saveRoute(id: string | null, name: string): Promise<void> {
    if (name.trim() === "") {
      this.toasts.show({ kind: "error", title: "El nombre de la ruta es obligatorio" });
      return;
    }
    this.fleet.setBusy(true);
    try {
      if (id === null) {
        await this.client.createRoute({ name: name.trim() });
        this.toasts.show({ kind: "success", title: "Ruta agregada" });
      } else {
        await this.client.updateRoute(id, { name: name.trim() });
        this.toasts.show({ kind: "success", title: "Ruta actualizada" });
      }
      await this.loadFleet();
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("route", error);
      this.toasts.show({ kind: "error", title: "No pudimos guardar la ruta", message: humanizeError(error) });
    } finally {
      this.fleet.setBusy(false);
    }
  }

  /** Importa el padrón desde el CSV del piloto (cabecera flexible, canal por defecto push). */
  private async importHouseholdsCsv(text: string): Promise<void> {
    const parsed = parseHouseholdsCsv(text);
    if (parsed.rows.length === 0) {
      this.toasts.show({
        kind: "error",
        title: "No pudimos leer el CSV",
        message: parsed.errors[0] ?? "Revisa el formato del archivo.",
        timeoutMs: 12000,
      });
      return;
    }
    try {
      const result = await this.client.bulkHouseholds(
        parsed.rows.map((row) => ({
          name: row.name,
          phone: row.phone,
          lat: row.lat,
          lng: row.lng,
          lead_minutes: row.leadMinutes,
          channels: row.channels,
        })),
      );
      const failed = result.failed.length + parsed.errors.length;
      this.toasts.show({
        kind: failed === 0 ? "success" : "info",
        title: `${result.created} ${result.created === 1 ? "hogar creado" : "hogares creados"}`,
        message: failed === 0 ? "Padrón actualizado." : `${failed} ${failed === 1 ? "fila" : "filas"} con problemas.`,
        timeoutMs: 9000,
      });
      const issues = [
        ...parsed.errors.slice(0, 2),
        ...result.failed.slice(0, 3).map((failure) => `Fila ${failure.index + 2}: ${failure.message}`),
      ];
      if (issues.length > 0) {
        this.toasts.show({
          kind: "error",
          title: "Revisa estas filas",
          message: issues.slice(0, 3).join(" · "),
          timeoutMs: 15000,
        });
      }
      await this.loadHouseholds();
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("import", error);
      this.toasts.show({ kind: "error", title: "No pudimos importar el padrón", message: humanizeError(error) });
    }
  }

  private async exportHouseholdsCsv(): Promise<void> {
    try {
      const rows = await this.fetchAllHouseholds();
      if (rows.length === 0) {
        this.toasts.show({ kind: "info", title: "No hay hogares para exportar" });
        return;
      }
      const content = toCsv(
        ["nombre", "telefono", "lat", "lng", "anticipacion", "canales", "creado"],
        rows.map((household) => [
          household.name,
          household.phone ?? "",
          household.lat,
          household.lng,
          household.lead_minutes,
          household.channels.join("|"),
          household.created_at,
        ]),
      );
      downloadCsv(`ecotracker-hogares-${new Date().toISOString().slice(0, 10)}.csv`, content);
      this.toasts.show({ kind: "success", title: `${rows.length} hogares exportados` });
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("export households", error);
      this.toasts.show({ kind: "error", title: "No pudimos exportar el padrón", message: humanizeError(error) });
    }
  }

  private async fetchAllHouseholds(): Promise<Household[]> {
    const out: Household[] = [];
    for (let offset = 0; offset < 5000; offset += 200) {
      const page = await this.client.households({ limit: 200, offset });
      out.push(...page.households);
      if (out.length >= page.total || page.households.length === 0) break;
    }
    return out;
  }

  private async exportAlertsCsv(): Promise<void> {
    try {
      const events = await this.fetchAllAlerts();
      if (events.length === 0) {
        this.toasts.show({ kind: "info", title: "No hay alertas para exportar" });
        return;
      }
      const content = toCsv(
        ["fecha", "hogar", "vehiculo", "ruta", "eta_segundos", "distancia_m", "canales"],
        events.map((event) => [
          event.created_at,
          event.household_name,
          event.vehicle_label,
          event.route_name,
          event.eta_seconds,
          event.distance_m,
          event.channels.join("|"),
        ]),
      );
      downloadCsv(`ecotracker-alertas-${new Date().toISOString().slice(0, 10)}.csv`, content);
      this.toasts.show({ kind: "success", title: `${events.length} alertas exportadas` });
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("export alerts", error);
      this.toasts.show({ kind: "error", title: "No pudimos exportar las alertas", message: humanizeError(error) });
    }
  }

  private async fetchAllAlerts(): Promise<NotificationEvent[]> {
    const out: NotificationEvent[] = [];
    const householdId = this.alertsFilter?.id;
    for (let offset = 0; offset < 5000; offset += 200) {
      const page = await this.client.notifications({ limit: 200, offset, householdId });
      out.push(...page.events);
      if (out.length >= page.total || page.events.length === 0) break;
    }
    return out;
  }

  private async loadDevices(): Promise<void> {
    this.devicesStatus = "loading";
    this.devices.setState({ status: "loading", devices: this.devicesList, error: null });
    try {
      const response = await this.client.devices();
      this.devicesList = response.devices;
      this.devicesStatus = "ready";
      this.devices.setState({ status: "ready", devices: response.devices, error: null });
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("devices", error);
      this.devicesStatus = "error";
      this.devices.setState({ status: "error", devices: this.devicesList, error: humanizeError(error) });
    }
  }

  private async loadPqrs(filter: string): Promise<void> {
    this.pqrsStatus = "loading";
    this.pqrs.setState({ status: "loading", items: [], total: 0, error: null });
    try {
      const response = await this.client.pqrs({ limit: 100, status: filter as PQRSStatus | "" });
      this.pqrsStatus = "ready";
      this.pqrs.setState({ status: "ready", items: response.items, total: response.total, error: null });
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("pqrs", error);
      this.pqrsStatus = "error";
      this.pqrs.setState({ status: "error", items: [], total: 0, error: humanizeError(error) });
    }
  }

  private async updatePqrs(id: string, status: PQRSStatus): Promise<void> {
    try {
      await this.client.updatePQRS(id, status);
      this.toasts.show({
        kind: "success",
        title: status === "cerrada" ? "PQRS cerrada" : "PQRS en proceso",
        message: "El estado quedó registrado.",
      });
      await this.loadPqrs(this.pqrs.filter());
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("pqrs update", error);
      this.toasts.show({ kind: "error", title: "No pudimos actualizar la PQRS", message: humanizeError(error) });
    }
  }

  private async createDevice(form: DeviceForm): Promise<void> {
    this.devices.setCreating(true);
    try {
      const label = form.label.trim();
      const issued = await this.client.createDevice({
        vehicle_id: form.vehicleId,
        route_id: form.routeId,
        ...(label === "" ? {} : { label }),
      });
      this.issuedDevice = issued;
      this.devices.setIssued(issued);
      this.toasts.show({
        kind: "success",
        title: "Rastreador registrado",
        message: "Copia la clave ahora: no se puede recuperar después.",
        timeoutMs: 12000,
      });
      await this.loadDevices();
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("createDevice", error);
      this.toasts.show({
        kind: "error",
        title: "No pudimos registrar el rastreador",
        message: humanizeError(error),
      });
    } finally {
      this.devices.setCreating(false);
    }
  }

  private async revokeDevice(device: Device): Promise<void> {
    const name = device.label.trim() === "" ? device.vehicle_id : device.label;
    const confirmed = await confirmAction({
      title: `¿Revocar «${name}»?`,
      message: "El equipo dejará de autenticar de inmediato y su clave no se puede recuperar.",
      confirmLabel: "Revocar equipo",
      danger: true,
    });
    if (!confirmed) return;
    try {
      await this.client.revokeDevice(device.id);
      this.toasts.show({ kind: "success", title: "Equipo revocado", message: "Su clave ya no funciona." });
      if (this.issuedDevice?.id === device.id) {
        this.issuedDevice = null;
        this.devices.setIssued(null);
      }
      await this.loadDevices();
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("revokeDevice", error);
      this.toasts.show({
        kind: "error",
        title: "No pudimos revocar el equipo",
        message: humanizeError(error),
      });
    }
  }

  private async copyDeviceKey(): Promise<void> {
    const key = this.issuedDevice?.device_key;
    if (key === undefined) return;
    const copied = await this.writeToClipboard(key);
    if (copied) {
      this.toasts.show({
        kind: "success",
        title: "Clave copiada",
        message: "Configúrala en el rastreador; no se mostrará de nuevo.",
      });
    } else {
      this.toasts.show({
        kind: "error",
        title: "No pudimos copiar la clave",
        message: `Anótala manualmente: ${key}`,
        timeoutMs: 15000,
      });
    }
  }

  private async loadHouseholds(retried = false): Promise<void> {
    const request = ++this.householdsRequest;
    const query = this.householdsQuery;
    const offset = this.householdsOffset;
    this.householdsStatus = "loading";
    this.households.setList({
      status: "loading",
      households: this.householdsList,
      total: this.householdsTotal,
      offset,
      limit: HOUSEHOLDS_PAGE_SIZE,
      query,
      error: null,
    });
    try {
      const data = await this.client.households({
        q: query === "" ? undefined : query,
        limit: HOUSEHOLDS_PAGE_SIZE,
        offset,
      });
      if (request !== this.householdsRequest) return;
      if (data.households.length === 0 && data.total > 0 && offset > 0 && !retried) {
        this.householdsOffset = Math.max(0, Math.floor((data.total - 1) / HOUSEHOLDS_PAGE_SIZE) * HOUSEHOLDS_PAGE_SIZE);
        await this.loadHouseholds(true);
        return;
      }
      this.householdsList = [...data.households];
      this.householdsTotal = data.total;
      this.householdsStatus = "ready";
      this.households.setList({
        status: "ready",
        households: this.householdsList,
        total: this.householdsTotal,
        offset,
        limit: HOUSEHOLDS_PAGE_SIZE,
        query,
        error: null,
      });
      await this.loadMapHouseholds(request);
      if (!this.hasFitted && this.householdsList.length > 0 && (this.live?.sessions.length ?? 0) === 0) {
        this.map.fitAll(this.live?.sessions ?? [], this.householdsList);
        this.hasFitted = true;
      }
    } catch (error) {
      if (isAbortError(error) || request !== this.householdsRequest) return;
      console.error("households", error);
      this.householdsStatus = "error";
      this.households.setList({
        status: "error",
        households: this.householdsList,
        total: this.householdsTotal,
        offset,
        limit: HOUSEHOLDS_PAGE_SIZE,
        query,
        error: humanizeError(error),
      });
    }
  }

  /** Mantiene el mapa con todos los hogares aunque la lista esté paginada o filtrada. */
  private async loadMapHouseholds(request: number): Promise<void> {
    if (this.householdsQuery === "" && this.householdsOffset === 0 && this.householdsTotal <= HOUSEHOLDS_PAGE_SIZE) {
      this.map.setHouseholds(this.householdsList);
      return;
    }
    try {
      const all = await this.client.households({ limit: HOUSEHOLDS_MAP_LIMIT });
      if (request !== this.householdsRequest) return;
      this.map.setHouseholds(all.households);
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("households:map", error);
      this.map.setHouseholds(this.householdsList);
    }
  }

  private searchHouseholds(query: string): void {
    if (query === this.householdsQuery) return;
    this.householdsQuery = query;
    this.householdsOffset = 0;
    void this.loadHouseholds();
  }

  private goToHouseholdsPage(offset: number): void {
    this.householdsOffset = Math.max(0, offset);
    void this.loadHouseholds();
  }

  private startEditHousehold(household: Household): void {
    this.stopPickMode();
    this.editingId = household.id;
    this.households.startEdit(household);
    this.map.setPickedLocation(household.lat, household.lng);
    this.map.focusHousehold(household.id);
  }

  private cancelEditHousehold(): void {
    this.editingId = null;
    this.households.stopEdit();
    this.stopPickMode();
    this.map.setPickedLocation(null, null);
  }

  private async generateCode(): Promise<void> {
    const selection = this.operation.getSelection();
    if (selection === null) {
      this.toasts.show({
        kind: "info",
        title: "Elige vehículo y ruta",
        message: "El código del turno se genera para un vehículo y una ruta.",
      });
      return;
    }
    this.operation.setGenerating(true);
    try {
      const code = await this.client.createCode({
        vehicle_id: selection.vehicleId,
        route_id: selection.routeId,
        ttl_minutes: CODE_TTL_MINUTES,
      });
      this.code = code;
      this.operation.setCode(code);
      this.toasts.show({
        kind: "success",
        title: `Código ${code.code} listo`,
        message: `${code.vehicle.label} · ${code.route.name} · vence ${formatCountdown(code.expires_at)}`,
      });
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("codes", error);
      this.toasts.show({
        kind: "error",
        title: "No se pudo generar el código",
        message: humanizeError(error),
      });
    } finally {
      this.operation.setGenerating(false);
    }
  }

  private async copyCode(): Promise<void> {
    const code = this.code;
    if (code === null) return;
    const copied = await this.writeToClipboard(code.code);
    if (copied) {
      this.toasts.show({ kind: "success", title: "Código copiado", message: "Compártelo con el conductor del turno." });
    } else {
      this.toasts.show({
        kind: "error",
        title: "No pudimos copiar el código",
        message: `Anótalo manualmente: ${code.code}`,
        timeoutMs: 12000,
      });
    }
  }

  private async writeToClipboard(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      try {
        const helper = document.createElement("textarea");
        helper.value = text;
        helper.setAttribute("readonly", "");
        helper.style.position = "fixed";
        helper.style.opacity = "0";
        document.body.append(helper);
        helper.select();
        const ok = document.execCommand("copy");
        helper.remove();
        return ok;
      } catch {
        return false;
      }
    }
  }

  private async saveHousehold(payload: CreateHouseholdRequest): Promise<void> {
    this.households.setSubmitting(true);
    this.households.setFormError(null);
    const editingId = this.editingId;
    try {
      if (editingId === null) {
        const created = await this.client.createHousehold(payload);
        this.households.resetForm();
        this.stopPickMode();
        this.map.setPickedLocation(null, null);
        this.householdsOffset = 0;
        this.toasts.show({
          kind: "success",
          title: `Hogar «${created.name}» creado`,
          message: "Recibirá el aviso cuando el camión esté cerca.",
        });
      } else {
        const updated = await this.client.updateHousehold(editingId, payload);
        this.editingId = null;
        this.households.stopEdit();
        this.stopPickMode();
        this.map.setPickedLocation(null, null);
        this.toasts.show({
          kind: "success",
          title: `Hogar «${updated.name}» actualizado`,
          message: "Los avisos usan la nueva información.",
        });
      }
      await this.loadHouseholds();
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("households:save", error);
      this.households.setFormError(humanizeError(error));
    } finally {
      this.households.setSubmitting(false);
    }
  }

  private async deleteHousehold(household: Household): Promise<void> {
    const confirmed = await confirmAction({
      title: "¿Borrar este hogar?",
      message: `Se eliminará «${household.name}» junto con sus alertas.`,
      detail: "No se puede deshacer.",
      confirmLabel: "Borrar hogar",
      danger: true,
    });
    if (!confirmed) return;

    this.households.setDeleting(household.id);
    try {
      await this.client.deleteHousehold(household.id);
      if (this.editingId === household.id) {
        this.editingId = null;
        this.households.stopEdit();
      }
      if (this.alertsFilter?.id === household.id) {
        this.alertsFilter = null;
        this.resetAlertsPages();
        this.alerts.setFilter(null);
      }
      this.toasts.show({
        kind: "success",
        title: `Hogar «${household.name}» borrado`,
        message: "También se eliminaron sus alertas.",
      });
      await this.loadHouseholds();
      this.statsPoller.runNow();
      this.alertsPoller.runNow();
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("households:delete", error);
      this.toasts.show({
        kind: "error",
        title: "No se pudo borrar el hogar",
        message: humanizeError(error),
      });
    } finally {
      this.households.setDeleting(null);
    }
  }

  private async clearDemoData(): Promise<void> {
    const confirmed = await confirmAction({
      title: "¿Limpiar los datos de prueba?",
      message: "Se borran hogares, alertas, recorridos y códigos creados en la demo.",
      detail: "La flota, las rutas y el operador se conservan. No se puede deshacer.",
      confirmLabel: "Limpiar datos",
      danger: true,
    });
    if (!confirmed) return;

    setBusy(this.demoClearButton, true);
    try {
      await this.client.deleteDemoData();
      this.editingId = null;
      this.households.stopEdit();
      this.stopPickMode();
      this.map.setPickedLocation(null, null);
      this.householdsQuery = "";
      this.householdsOffset = 0;
      this.households.setSearchQuery("");
      this.alertsFilter = null;
      this.resetAlertsPages();
      this.alerts.setFilter(null);
      this.toasts.show({
        kind: "success",
        title: "Datos de prueba eliminados",
        message: "El panel queda en blanco; la flota sigue disponible.",
      });
      await this.loadHouseholds();
      this.readyPoller.runNow();
      this.livePoller.runNow();
      this.alertsPoller.runNow();
      this.statsPoller.runNow();
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("demo-data", error);
      this.toasts.show({
        kind: "error",
        title: "No se pudieron limpiar los datos",
        message: humanizeError(error),
      });
    } finally {
      setBusy(this.demoClearButton, false);
    }
  }

  private async requestEta(household: Household): Promise<void> {
    this.households.setEta(household.id, { status: "pending" });
    try {
      const result = await this.client.householdEta(household.id);
      this.households.setEta(household.id, { status: "done", result });
      this.map.focusHousehold(household.id);
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("eta", error);
      this.households.setEta(household.id, { status: "error", message: humanizeError(error) });
    }
  }

  private togglePickMode(): void {
    if (this.map.isPicking) {
      this.stopPickMode();
      return;
    }
    this.map.startPick((lat, lng) => {
      this.households.setLocation(lat, lng);
      this.households.setFormError(null);
      this.map.setPickedLocation(lat, lng);
      this.stopPickMode();
      this.toasts.show({
        kind: "success",
        title: "Ubicación marcada",
        message: "Ponle nombre al hogar y guárdalo para activar sus avisos.",
      });
    });
    this.households.setPicking(true);
    setHidden(this.mapPick, false);
    this.mapContainer.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  private stopPickMode(): void {
    this.map.cancelPick();
    this.households.setPicking(false);
    setHidden(this.mapPick, true);
  }

  private useMyLocation(): void {
    if (!("geolocation" in navigator)) {
      this.toasts.show({
        kind: "error",
        title: "Tu navegador no admite ubicación",
        message: "Marca el hogar directamente en el mapa.",
      });
      return;
    }
    this.households.setLocationBusy(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.households.setLocationBusy(false);
        const { latitude, longitude } = position.coords;
        this.households.setLocation(latitude, longitude);
        this.households.setFormError(null);
        this.map.setPickedLocation(latitude, longitude);
        this.map.panTo(latitude, longitude);
        this.toasts.show({
          kind: "success",
          title: "Ubicación lista",
          message: "Confirma el nombre y guarda el hogar.",
        });
      },
      (error) => {
        this.households.setLocationBusy(false);
        this.toasts.show({
          kind: "error",
          title: "No pudimos usar tu ubicación",
          message: GEOLOCATION_MESSAGES[error.code] ?? "Marca el hogar directamente en el mapa.",
        });
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 },
    );
  }

  private clearLocation(): void {
    this.stopPickMode();
    this.households.setLocation(null, null);
    this.map.setPickedLocation(null, null);
  }

  private showMapError(message: string): void {
    clear(this.mapError);
    this.mapContainer.parentElement?.classList.add("map-wrap--error");
    const text = el("span", { className: "map__error-text" });
    text.append(icon("i-alert"), el("span", { text: message }));
    const retry = el("button", { className: "btn btn--ghost btn--sm" });
    retry.type = "button";
    retry.append(icon("i-refresh"), el("span", { text: "Reintentar" }));
    retry.addEventListener("click", () => this.livePoller.runNow());
    this.mapError.append(text, retry);
    setHidden(this.mapError, false);
  }

  private hideMapError(): void {
    setHidden(this.mapError, true);
    this.mapContainer.parentElement?.classList.remove("map-wrap--error");
  }

  private initSettings(): void {
    const deployed = !isLocalDevelopment();
    this.baseUrlField.hidden = deployed;
    this.baseUrlNote.hidden = !deployed;
    setText(this.settingsMode, deployed ? "Modo despliegue" : "Modo desarrollo");
    setText(this.readyDetail, "Comprobando el estado…");
    this.demoClearButton.addEventListener("click", () => void this.clearDemoData());

    const settingsError = el("p", { className: "form__error" });
    settingsError.hidden = true;
    this.apiKeyInput.closest(".field")?.insertAdjacentElement("afterend", settingsError);

    const showSettingsError = (message: string | null): void => {
      settingsError.hidden = message === null;
      if (message !== null) {
        clear(settingsError);
        settingsError.append(icon("i-alert"), el("span", { text: message }));
      }
    };

    this.configForm.addEventListener("submit", (event) => {
      event.preventDefault();
      const baseUrl = normalizeBaseUrl(this.baseUrlInput.value);
      const apiKey = this.apiKeyInput.value.trim();
      if (!deployed && !isValidBaseUrl(baseUrl)) {
        showSettingsError("Escribe una dirección válida, por ejemplo http://localhost:8080.");
        return;
      }
      if (apiKey === "") {
        showSettingsError("La API key del operador no puede quedar vacía.");
        return;
      }
      showSettingsError(null);
      this.baseUrlInput.value = baseUrl;
      this.apiKeyInput.value = apiKey;
      this.applyConfig({ baseUrl: deployed ? this.config.baseUrl : baseUrl, apiKey }, true);
      this.settings.open = false;
      this.toasts.show({ kind: "success", title: "Ajustes guardados", message: "El panel se reconectó con estos datos." });
    });

    this.configResetButton.addEventListener("click", () => {
      const fallback = defaultConfig();
      this.baseUrlInput.value = fallback.baseUrl;
      this.apiKeyInput.value = fallback.apiKey;
      showSettingsError(null);
      this.applyConfig(fallback, true);
      this.settings.open = false;
      this.toasts.show({ kind: "info", title: "Valores restaurados", message: "Se usan los valores por defecto de este entorno." });
    });

    const logoutButton = document.getElementById("config-logout") as HTMLButtonElement | null;
    logoutButton?.addEventListener("click", () => {
      // Cerrar sesión: se elimina la API key y vuelve la puerta de acceso.
      const fallback = defaultConfig();
      this.apiKeyInput.value = "";
      saveConfig({ ...fallback, apiKey: "" });
      window.location.reload();
    });

    document.addEventListener("click", (event) => {
      const target = event.target;
      if (this.settings.open && target instanceof Node && !this.settings.contains(target)) {
        this.settings.open = false;
      }
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.settings.open) this.settings.open = false;
    });
  }
}

export function startDashboard(): void {
  new Dashboard().start();
}
